/**
 * Feature flags — required policy.
 *
 * Every user-visible feature must register a flag here BEFORE it ships.
 * The flag gates whatever nav item, route, button, or panel exposes the
 * feature; default OFF until the corresponding backend endpoint is live
 * in staging and the flag flips to ON in `frogasia/lms/web/<env>`.
 *
 * How values resolve:
 *
 *   1. Build time: Vite inlines `import.meta.env.VITE_FEATURE_<KEY>`
 *      from the env vars passed to `pnpm build` (via `docker build
 *      --build-arg`). The deploy workflow sources these from the AWS
 *      secret `frogasia/lms/web/<env>` — see docs/DEPLOY.md.
 *   2. Local dev: set `VITE_FEATURE_<KEY>=true` in `packages/web/.env`
 *      to flip a flag while you work on it.
 *   3. Default: OFF unless an explicit `defaultValue: true` is passed
 *      to `flag()` for things considered universally enabled (e.g.
 *      core leave-application surface).
 *
 * To add a flag:
 *   - add a line to the `flags` object below,
 *   - reference it via `flags.foo` in the nav config / router / page,
 *   - add `VITE_FEATURE_FOO` to the AWS secret when ready to ship.
 *
 * To retire a flag (feature has shipped + soaked):
 *   - delete the line here, delete every `flags.foo` reference,
 *   - remove `VITE_FEATURE_FOO` from the AWS secret.
 */

function flag(name: string, defaultValue = false): boolean {
  // import.meta.env is typed as Record<string, string | boolean | undefined>
  // by Vite, but only string is actually emitted at build time.
  const env = import.meta.env as unknown as Record<string, string | undefined>
  const raw = env[`VITE_FEATURE_${name}`]
  if (raw == null || raw === '') return defaultValue
  return raw === 'true' || raw === '1'
}

export const flags = {
  /** White-label runtime chrome: drive logo / company name / tab title +
   *  the color theme from the `org_config` row (Phase 2 of the white-label
   *  effort) instead of hardcoded values. Default ON (kill-switch); flip
   *  OFF via `VITE_FEATURE_WHITE_LABEL_CHROME=false` in `frogasia/lms/web/<env>`
   *  to fall back to the static YTL branding baked into the components.
   *  Gates the apply-theme / document-branding side effects only — the
   *  components still read `useOrgConfig()`, whose defaults equal today's
   *  YTL values, so YTL is visually identical either way. */
  whiteLabelChrome: flag('WHITE_LABEL_CHROME', true),

  /** White-label admin hub (`/admin`) — one place for branding, theme, user
   *  roles, and links to the existing config pages. admin+ only. Default ON
   *  (kill-switch); flip OFF via `VITE_FEATURE_ADMIN_HUB=false`. */
  adminHub: flag('ADMIN_HUB', true),

  /** HR Employees CRUD page (`/hr/employees`). Backed by
   *  `GET/POST/PATCH /api/v1/employees`. Defaults ON; flip OFF via
   *  `VITE_FEATURE_HR_EMPLOYEES=false` in the AWS web secret if the
   *  page regresses or the api routes aren't ready in a given env. */
  hrEmployees: flag('HR_EMPLOYEES', true),

  /** HR-editable "Slack email" override on the employee edit form
   *  (`/hr/employees`) for employees whose Slack profile email differs from
   *  their LMS work email — backed by `employees.slack_email` via
   *  `PATCH /api/v1/employees/{id}`. Defaults ON (kill-switch); flip OFF via
   *  `VITE_FEATURE_SLACK_EMAIL_OVERRIDE=false` in `frogasia/lms/web/<env>`. */
  slackEmailOverride: flag('SLACK_EMAIL_OVERRIDE', true),

  /** HR Org Chart page (`/hr/org-chart`) — Track B MVP. Single-page
   *  read-only org chart showing employees grouped by team plus the
   *  per-employee approval chain (who approves them / who they
   *  approve), sourced from `approval_assignments` via
   *  `GET /api/v1/hr/org-chart`. HR / Director / Exec Director only.
   *  Defaults ON for the pilot so HR can verify approval routing
   *  without env-var plumbing; kill-switch OFF via
   *  `VITE_FEATURE_HR_ORG_CHART=false` in `frogasia/lms/web/<env>`.
   *  The api side also needs `HR_ORG_CHART_ENABLED=true` (Settings
   *  default mirrors this to true). */
  hrOrgChart: flag('HR_ORG_CHART', true),

  /** Temporary approver override — the "Approver cover" tab of the EA hub
   *  (`/hr/ea?tab=approver-cover`). HR reroutes an approver's inbox to a
   *  stand-in for a date window; NEW leaves filed in the window route to the
   *  stand-in (bind-at-submit). Backed by `/api/v1/admin/approver-overrides`.
   *  Defaults ON for the pilot so HR gets the tab without env plumbing;
   *  kill-switch OFF via `VITE_FEATURE_APPROVER_OVERRIDE=false`. The api side
   *  mirrors this default (`APPROVER_OVERRIDE_ENABLED=true`); both gates ON. */
  approverOverride: flag('APPROVER_OVERRIDE', true),

  /** HR Org Structure admin page (`/hr/org-structure`) — form/table CRUD for
   *  the org-unit tree (create/rename/move/set-head/delete) plus team→unit
   *  assignment. The admin surface for structure; the org chart stays
   *  read-only. Backed by `/api/v1/hr/org-units` + `PATCH /api/v1/admin/teams`.
   *  Defaults ON so HR sees the "Manage structure" nav item without env-var
   *  plumbing; kill-switch OFF via
   *  `VITE_FEATURE_ORG_STRUCTURE_ADMIN=false` in `frogasia/lms/web/<env>`. */
  orgStructureAdmin: flag('ORG_STRUCTURE_ADMIN', true),

  /** HR Blackouts management (`/hr/blackouts`). Backed by
   *  `/api/v1/blackouts`. Defaults ON. */
  hrBlackouts: flag('HR_BLACKOUTS', true),

  /** HR Calendar connector (`/hr/calendar`). Status + Test for the Microsoft
   *  Graph shared-calendar sync. Backed by `/api/v1/hr/calendar`. Defaults ON. */
  hrCalendarConnector: flag('HR_CALENDAR_CONNECTOR', true),

  /** HR Public Holidays management (`/hr/holidays`). Backed by
   *  `/api/v1/admin/public-holidays`. Defaults OFF in development —
   *  flip ON via `VITE_FEATURE_HR_HOLIDAYS=true` once the page is
   *  reviewed. Closes the 7 May "fix holiday dates / CRUD upload" ask. */
  hrHolidays: flag('HR_HOLIDAYS', true),

  /** HR Settings page (`/hr/settings`). Backed by `/api/v1/settings`.
   *  Surface for the global policy + integration toggles. Defaults
   *  ON; flip OFF if the schema needs an emergency rollback. */
  hrSettings: flag('HR_SETTINGS', true),

  /** HR Payroll Export (`/hr/payroll`). Backed by
   *  `GET /api/v1/payroll/export.csv`. Defaults ON; flip OFF only
   *  if a downstream payroll-team CSV consumer changes shape and
   *  we need to suppress the page without a deploy. */
  hrPayroll: flag('HR_PAYROLL', true),

  /** Audit Log (`/hr/audit`). Backed by `GET /api/v1/audit`.
   *  HR / director / exec_director only; the page itself enforces
   *  the role check via `<RequireRole minimum="hr">` plus the api
   *  rejecting < director-or-HR with 403. Defaults ON. */
  hrAudit: flag('HR_AUDIT', true),

  /** "Viewing As" subheader (HR / director / exec_director only) —
   *  read-only impersonation that re-renders the sidebar + role-aware
   *  panels as if the selected employee were signed in. Mutations
   *  still travel under the real session; this is purely a client-side
   *  view filter for diagnostics + empathy. Defaults ON in dev/staging;
   *  add `VITE_FEATURE_VIEWING_AS=false` to `frogasia/lms/web/<env>`
   *  in AWS Secrets Manager to flip OFF in production until reviewed. */
  viewingAs: flag('VIEWING_AS', true),

  /** Apply-page "Closest alternatives" date suggestions. When the chosen
   *  window clashes, `IlmuNarrativeBlock` lists up to 3 alternative windows
   *  with Use buttons (fed by `POST /api/v1/leaves/window-recommendations`).
   *  Defaults OFF; flip ON via `VITE_FEATURE_APPLY_ALTERNATIVE_DATES=true`
   *  in `frogasia/lms/web/<env>`. The impact facts still render either way. */
  applyAlternativeDates: flag('APPLY_ALTERNATIVE_DATES', false),

  /** Conversation Drafter — slice A3. From the Patterns view, manager
   *  clicks an employee row → "Draft Conversation" → modal renders five
   *  editable fields (`title`, `opening`, `talkingPoints`, `toneWarning`,
   *  `nextStep`). Closes the PM-doc gap for AI-assisted manager
   *  conversations on flagged patterns. Send-to-Slack lands in the A3
   *  follow-up. Defaults OFF; flip ON via
   *  `VITE_FEATURE_AI_CONVERSATION_DRAFTER=true` in
   *  `frogasia/lms/web/<env>`. */
  aiConversationDrafter: flag('AI_CONVERSATION_DRAFTER', true),

  /** HR cancel-approved-leave page (`/hr/cancel-leave`) — slice B2.
   *  Allows HR to void an already-approved leave and refund the taken
   *  days back to the employee's balance. Backed by
   *  `POST /api/v1/leaves/{id}/hr-cancel`. Defaults OFF; flip ON via
   *  `VITE_FEATURE_HR_CANCEL_APPROVED=true` in
   *  `frogasia/lms/web/<env>` once the api route is live. */
  hrCancelApproved: flag('HR_CANCEL_APPROVED', true),


  /** Redesigned HomePage — replaces the legacy redirect with a thin
   *  role-aware lobby (PageHead + role-specific navigation cards
   *  pointing at the next step the user would take). When OFF, the
   *  page redirects to /my-leave / /manager / /hr/employees as before.
   *  Defaults OFF; flip ON via `VITE_FEATURE_REDESIGNED_HOME=true` in
   *  `frogasia/lms/web/<env>` once the design has settled. */
  redesignedHome: flag('REDESIGNED_HOME', true),


  /** AI override-reason analytics tile — Sprint 6 F6.
   *  Renders the OverrideRateTile above the audit log on `/hr/audit`.
   *  Backed by `GET /api/v1/hr/override-rate`. HR / Director / Exec Director
   *  only (the page itself is already role-gated). Defaults OFF — measures
   *  overrides of the priority scores, which are retired; flip ON via
   *  `VITE_FEATURE_AI_OVERRIDE_REASON=true` in `frogasia/lms/web/<env>`. */
  aiOverrideReason: flag('AI_OVERRIDE_REASON', false),

  /** ILMU-generated wellbeing-card narrative — GET /api/v1/me/wellbeing-narrative
   *  (own card) and GET /api/v1/employees/{id}/wellbeing-narrative (HR
   *  "Viewing as"). When ON the Leave Rhythm card's headline + summary come
   *  from the ai-service hop (grounded in §2.3 catalog copy + §2.2 band action,
   *  24h-cached); when OFF the card falls back to deterministic band copy. The
   *  band/score verdict always stays with the rule engine. Restored post-SG#1.
   *  Defaults ON; kill-switch OFF via
   *  `VITE_FEATURE_ILMU_WELLBEING_NARRATIVE=false` in `frogasia/lms/web/<env>`. */
  ilmuWellbeingNarrative: flag('ILMU_WELLBEING_NARRATIVE', true),

  /** HOD Forecast Digest page (`/hod/digest`) — Sprint 7 F1.
   *  Shows a coverage-breach forecast for a HOD's teams over a configurable
   *  window (7 / 14 / 21 / 30 days). Backed by
   *  `GET /api/v1/forecast/digest`. HOD role views own digest; HR / Director /
   *  Exec Director pick any HOD from a dropdown populated by
   *  `GET /api/v1/employees?role=hod&active=true`.
   *  Defaults OFF; flip ON via `VITE_FEATURE_HOD_FORECAST_DIGEST=true` in
   *  `frogasia/lms/web/<env>` once the api route is live in staging. */
  hodForecastDigest: flag('HOD_FORECAST_DIGEST', true),

  /** Background jobs console (`/admin/jobs`, admin+ sidebar entry) —
   *  runtime pause/resume + scheduling for the managed arq crons and the
   *  weekly digest notifications. Backed by `GET/PATCH /api/v1/admin/jobs`.
   *  Defaults ON; kill-switch OFF via
   *  `VITE_FEATURE_ADMIN_BACKGROUND_JOBS=false` in `frogasia/lms/web/<env>`. */
  adminBackgroundJobs: flag('ADMIN_BACKGROUND_JOBS', true),


  /** Inline citation expander — Sprint 1 Feature 5.
   *  Renders CitationChip rows below AIPanel prose, letting HR drill
   *  into the SQL + preview rows behind each claim.  When OFF, the
   *  citations footer in AIPanel is suppressed even if citations are
   *  passed as props.  Defaults OFF; flip ON via
   *  `VITE_FEATURE_CITATION_EXPANDER=true` in
   *  `frogasia/lms/web/<env>` once the InsightsAIDigest wiring is
   *  validated in staging. */
  citationExpander: flag('CITATION_EXPANDER', true),

  /** Email-action inline approval confirmation — Sprint 9.
   *  Renders a calm, centered confirmation card at `/email-action/confirm`
   *  when a manager clicks an approve / discuss / hold action link from an
   *  email notification. Public route — no auth required. Reads
   *  `?status=approved|discuss|hold|expired|invalid&leave_id=...&actor_name=...`
   *  query params (Zod-validated). Defaults OFF; flip ON via
   *  `VITE_FEATURE_INLINE_APPROVAL_CARD=true` in `frogasia/lms/web/<env>`
   *  once the api generates the signed email-action links. */
  inlineApprovalCard: flag('INLINE_APPROVAL_CARD', true),

  /** Manager Dashboard date-range filter — kill-switch (retrofit).
   *  When ON, renders a DateRangeFilter in the Manager Dashboard page
   *  head and scopes Pending + In-window counts to the selected range.
   *  "On leave today" remains point-in-time and is never re-scoped.
   *  Backed by `from_date` / `to_date` query params on
   *  `GET /api/v1/dashboard`. Defaults ON (kill-switch on an existing page).
   *  TODO: add `VITE_FEATURE_MANAGER_DASHBOARD_DATE_FILTER` to
   *  `frogasia/lms/web/<env>` in AWS Secrets Manager (ap-southeast-5)
   *  before deploying to staging — default ON, set to `false` to suppress. */
  managerDashboardDateFilter: flag('MANAGER_DASHBOARD_DATE_FILTER', true),

  /** Manager queue approve-undo toast — Wave 5 (2026-05-20).
   *  Adds an Undo button to the toast shown after a manager approves a
   *  leave from the queue row. 60-second server-enforced window via
   *  POST /api/v1/leaves/{id}/decision/revoke. Slack DM to the employee
   *  is deferred 60s + worker re-reads status to skip stale publishes.
   *  Defaults OFF to gate the worker-defer change behind a flag. */
  managerApproveUndo: flag('MANAGER_APPROVE_UNDO', true),

  /** Manager queue "Decide today" segmented filter — Wave 4 (2026-05-20).
   *  Adds three filter chips above the queue (Decide today, All, Emergency)
   *  with the rule: "submitted ≥3 working days ago, or starts within 2
   *  working days." Pure FE — no api change. Defaults OFF to stage the
   *  rollout (Hafiz first, then HR validation). */
  managerDecideTodaySegmented: flag('MANAGER_DECIDE_TODAY_SEGMENTED', true),

  /** Manager queue row predictive chips — Wave 3 (2026-05-20).
   *  Surfaces the three server-computed signals (blackoutProximity,
   *  clusterImpact, holidayAdjacency) on each pending leave row via the
   *  single-token chip selector. Fields always come down on the wire
   *  (server-side cost is bounded — at most three small queries per page);
   *  the flag only gates FE consumption so we can roll out per role/env
   *  without redeploying the api.
   *  Defaults OFF until Hafiz + one HR validate on staging. */
  managerRowPredictiveChips: flag('MANAGER_ROW_PREDICTIVE_CHIPS', true),

  /** HR Team coverage thresholds section on HrSettingsPage — 2026-05-13.
   *  Adds an inline-editable list of every team's `slot_pct` (absence cap)
   *  to the HR Settings page. HR can tune the maximum percentage of the team
   *  who may be on leave at the same time. Backed by
   *  `PATCH /api/v1/admin/teams/{id}`. Defaults OFF (new feature).
   *  TODO: add `VITE_FEATURE_TEAM_COVERAGE_HR_CONTROL` to
   *  `frogasia/lms/web/<env>` in AWS Secrets Manager (ap-southeast-5)
   *  once the api route is live in staging. */
  teamCoverageHrControl: flag('TEAM_COVERAGE_HR_CONTROL', true),

  /** HR Teams management page (`/hr/teams`) — comprehensive Team CRUD
   *  under HR control: list every team (headcount + manager + coverage),
   *  create, rename, and delete (the last guarded while a team has
   *  members). Replaces the "I typo'd a team and can't remove it" gap:
   *  the org-chart's edit-mode × is the only delete affordance, and the
   *  name was never editable, so this gives HR a single, obvious home.
   *  Backed by `POST`/`PATCH`/`DELETE /api/v1/admin/teams/{id}` (all
   *  already live + audit-logged) and `GET /api/v1/teams`. Defaults ON
   *  by default (kill-switch posture — wraps already-shipped routes);
   *  flip OFF via `VITE_FEATURE_HR_TEAMS_MANAGEMENT=false` in
   *  `frogasia/lms/web/<env>`. */
  hrTeamsManagement: flag('HR_TEAMS_MANAGEMENT', true),

  /** HR Replacement Grants admin page (`/hr/replacement-grants`) — RFC 2026-05-13.
   *  Allows HR to create Replacement leave grants manually for employees
   *  who worked on non-working days (Saturdays, public holidays, or
   *  manager-approved overtime). Backed by
   *  `POST/GET /api/v1/admin/replacement-grants`. Defaults OFF; flip ON via
   *  `VITE_FEATURE_REPLACEMENT_GRANTS_ADMIN_UI=true` in
   *  `frogasia/lms/web/<env>` once the api route is live in staging. */
  replacementGrantsAdminUi: flag('REPLACEMENT_GRANTS_ADMIN_UI', true),

  /** Booking-window enforcement — advance booking cap (migration 0056).
   *  When ON: employees cannot apply leave more than 6 months ahead OR past
   *  end of current calendar year (whichever is sooner). Retro leave types
   *  (medical, compassionate) are exempt. Probationary employees are capped
   *  to their probation end date. HR can grant a per-employee override.
   *  FE clamps the date picker; BE enforces via the rule engine. Defaults
   *  OFF; flip ON via `VITE_FEATURE_BOOKING_WINDOW_ENFORCEMENT=true` in
   *  `frogasia/lms/web/<env>` once the api and migration 0056 are live in
   *  staging.
   *  TODO: add `VITE_FEATURE_BOOKING_WINDOW_ENFORCEMENT` to
   *  `frogasia/lms/web/<env>` in AWS Secrets Manager (ap-southeast-5). */
  bookingWindowEnforcement: flag('BOOKING_WINDOW_ENFORCEMENT', true),

  /** Local-password reset surfaces — 2026-05-14.
   *  When ON: surfaces three flows that depend on the dev-password bridge
   *    1. Self-serve "Change password" page at `/me/account` (any role).
   *    2. HR-initiated reset + force-change actions on each row of
   *       `/hr/employees` (modal that shows the temp password once).
   *    3. `/change-password-required` interstitial that forces a rotation
   *       when `account.mustResetPassword` is true on the session user.
   *  Backed by `POST /api/v1/auth/password/{change,reset,force-change}`.
   *  Entra-only users see no password UI — the BE returns 409 on the
   *  HR endpoints for accounts without a `credential` provider row.
   *  Defaults OFF; flip ON via `VITE_FEATURE_PASSWORD_RESET=true` in
   *  `frogasia/lms/web/<env>` once migration 0059 is live in staging.
   *  TODO: add `VITE_FEATURE_PASSWORD_RESET` to
   *  `frogasia/lms/web/<env>` in AWS Secrets Manager (ap-southeast-5). */
  passwordReset: flag('PASSWORD_RESET', true),

  /** Per-date half-day selection on the apply form — migration 0057.
   *  When ON: HalfDayPicker renders one AM/PM/Full row per working day in
   *  the selected range, letting employees mark any day as a half-day (not
   *  only the first and last). The submit payload sends `halfDays: [...]`
   *  to the BE which persists each slot in `leave_half_days` and uses the
   *  count for duration maths. When OFF: legacy Start/End HalfDayRow UI
   *  is shown (existing behaviour, only first/last day selectable).
   *  BE accepts the `halfDays` array regardless of this flag — the schema
   *  change is backward-compatible.
   *  Defaults OFF; flip ON via `VITE_FEATURE_HALF_DAY_PER_DATE=true` in
   *  `frogasia/lms/web/<env>` once migration 0057 is live in staging.
   *  TODO: add `VITE_FEATURE_HALF_DAY_PER_DATE` to
   *  `frogasia/lms/web/<env>` in AWS Secrets Manager (ap-southeast-5). */
  halfDayPerDate: flag('HALF_DAY_PER_DATE', true),

  /** HR Employee Flags (`/hr/employees/:id/flags`) — migration 0062.
   *  AI-detected and HR-manual observational flags about employees.
   *  HR / Director / ExecDirector only. Active-flag count badge appears
   *  on the employee list when ON. Backed by
   *  `GET/POST /api/v1/hr/employees/{id}/flags` and
   *  `PATCH /api/v1/hr/employees/{id}/flags/{flagId}/resolve`.
   *  Defaults OFF; flip ON via `VITE_FEATURE_HR_EMPLOYEE_FLAGS=true` in
   *  `frogasia/lms/web/<env>` once migration 0062 is live in staging.
   *  TODO: add `VITE_FEATURE_HR_EMPLOYEE_FLAGS` to
   *  `frogasia/lms/web/<env>` in AWS Secrets Manager (ap-southeast-5). */
  hrEmployeeFlags: flag('HR_EMPLOYEE_FLAGS', true),

  /** HR Employees directory enhancements (`/hr/employees`): a "Joined" column
   *  in the table plus a row of clickable filter cards above it (Contractual /
   *  Probationers / Anniversary this month). Cards show live counts from
   *  `GET /api/v1/employees/directory-stats` and, when clicked, narrow the list
   *  via the new `employmentType` / `probation` / `anniversaryMonth` query
   *  params on `GET /api/v1/employees`. Defaults ON (kill-switch); flip OFF via
   *  `VITE_FEATURE_EMPLOYEE_DIRECTORY_ENHANCEMENTS=false` in
   *  `frogasia/lms/web/<env>` if the cards or column regress. */
  employeeDirectoryEnhancements: flag('EMPLOYEE_DIRECTORY_ENHANCEMENTS', true),

  /** HR "Leaves" hub (`/hr/leaves`) — one sidebar entry with in-page tabs:
   *  Applications (org-wide leave list + cancel-approved row action), Balances
   *  (read-only wide roster across all leave types, backed by
   *  `GET /api/v1/leave-credits/balances/matrix`, with per-row Adjust),
   *  Entitlement (per-employee credit admin — adjust, defaults, annual reset,
   *  import), and Replacement Grants (grant / edit / cancel replacement leave).
   *  Replaces the former "Leave Applications" + "Adjustments" nav items — no
   *  legacy fallback. Default ON (kill-switch posture); set
   *  `VITE_FEATURE_HR_LEAVES=false` in `frogasia/lms/web/<env>` to hide it. */
  hrLeaves: flag('HR_LEAVES', true),

  /** Decision-Surface v2 (Phases 1–3 of the manager review redesign).
   *  When ON, the LeaveDetailPage renders Zones 1 (Action Required),
   *  2 (Cited Conditions), 4 (Requester View) and 5 (Audit Hook)
   *  alongside the legacy Impact preview. Zone 3 (AI context) ships
   *  in a follow-up phase once the structured context_items[]
   *  AI-service rewrite lands.
   *  Defaults ON — the slice has soaked through Phases 1 + 2 and is
   *  safe for default-on rendering across all approver roles. Flip
   *  OFF via `VITE_FEATURE_DECISION_SURFACE_V2=false` in
   *  `frogasia/lms/web/<env>` if a kill-switch is ever needed.
   *  See artifacts/agent-conversation-showcase/INSTRUCTIONS.md § 16. */
  decisionSurfaceV2: flag('DECISION_SURFACE_V2', true),

  /** HR Pattern Detection admin page (`/hr/pattern-detection`) — slice 16.
   *  Full control panel for the 8 leave-pattern detectors (frequent short
   *  absences, Monday/Friday clustering, pre/post-holiday spikes, etc.).
   *  HR / Director / Exec Director only. Backed by
   *  `GET /api/v1/pattern-detection/summary` and
   *  `GET /api/v1/pattern-detection/employees`.
   *  Defaults OFF; flip ON via
   *  `VITE_FEATURE_PATTERN_DETECTION_ADMIN=true` in
   *  `frogasia/lms/web/<env>` once the api routes are live in staging. */
  patternDetectionAdmin: flag('PATTERN_DETECTION_ADMIN', true),

  /** Blackout Reset panel (PRD §7) — inline panel inside the Blackouts tab.
   *  When a blackout is declared, HR can see which approved leaves overlap
   *  the window and reset them to Pending for manager re-approval.
   *  Backed by `GET /api/v1/blackouts/{id}/overlapping-leaves` and
   *  `POST /api/v1/leaves/{id}/reset-to-pending`. Defaults ON per PRD §7
   *  requirement — the feature is required and not gated behind a rollout.
   *  Flip OFF via `VITE_FEATURE_BLACKOUT_RESET_PANEL=false` in
   *  `frogasia/lms/web/<env>` if a kill-switch is needed. */
  blackoutResetPanel: flag('BLACKOUT_RESET_PANEL', true),

  /** Blackout requester-reconsider flow (Slice C).
   *  When a new blackout overlaps an approved leave, the reconciler now
   *  flips the leave to PENDING with `pending_reason='blackout_reset'`.
   *  This flag toggles the employee-side "Keep or Cancel" banner that
   *  surfaces on the home + leave detail pages. Defaults ON — replaces
   *  the legacy NEEDS_REVIEW manager-only flow; kill-switch via
   *  `VITE_FEATURE_BLACKOUT_REQUESTER_RECONSIDER=false` in
   *  `frogasia/lms/web/<env>`. */
  blackoutRequesterReconsider: flag('BLACKOUT_REQUESTER_RECONSIDER', true),

  /** Blackout resubmit flow for annual leave (RFC 004 §4.2).
   *  When a new blackout overlaps a previously-approved annual leave,
   *  the reconciler moves the leave to NEEDS_REVIEW with a
   *  5-business-day Cancel-or-Resubmit deadline. This flag gates the
   *  employee-side resubmit / cancel banner plus the manager-side
   *  recommendation form. Backend routes
   *  (`POST /api/v1/leaves/{id}/blackout/{resubmit,cancel}`,
   *  `POST /api/v1/leaves/{id}/manager-recommendation`) are always
   *  registered but inert when no row is in the relevant state. Defaults
   *  OFF until the new UI lands; flip ON via
   *  `VITE_FEATURE_BLACKOUT_RESUBMIT_FLOW=true` in
   *  `frogasia/lms/web/<env>`. */
  // Default ON per session preference (kill-switch posture); set
  // `VITE_FEATURE_BLACKOUT_RESUBMIT_FLOW=false` in
  // `frogasia/lms/web/<env>` to disable.
  blackoutResubmitFlow: flag('BLACKOUT_RESUBMIT_FLOW', true),

  /** Peak Period 104-pt scoring (RFC 005) — HR-managed peak-period catalogue
   *  at `/hr/peak-periods`. Backed by `/api/v1/hr/peak-periods` (CRUD).
   *  (The former priority-scoring-weights tuner card on `/hr/settings` and
   *  its `/api/v1/hr/priority-weights` route were removed with the
   *  priority-tuning feature.) HR / Director / Exec Director
   *  only. Defaults OFF until UAT — flip ON via
   *  `VITE_FEATURE_PEAK_PERIOD_104PT_SCORING=true` in
   *  `frogasia/lms/web/<env>` once the api routes are live in staging.
   *  TODO: add `VITE_FEATURE_PEAK_PERIOD_104PT_SCORING` to
   *  `frogasia/lms/web/<env>` in AWS Secrets Manager (ap-southeast-5). */
  // Default OFF — priority scoring is retired; set
  // `VITE_FEATURE_PEAK_PERIOD_104PT_SCORING=true` in
  // `frogasia/lms/web/<env>` to re-enable.
  peakPeriod104ptScoring: flag('PEAK_PERIOD_104PT_SCORING', false),

  /** Home AI cards (`/`) — role-aware mix of Action + AI cards backed by
   *  `GET /api/v1/home/insights`. Defaults OFF per the LMS feature-flag
   *  standard; flip ON via `VITE_FEATURE_HOME_AI_CARDS=true` in
   *  `frogasia/lms/web/<env>`. Spec at
   *  docs/superpowers/specs/2026-05-21-home-ai-cards-design.md. */
  homeAiCards: flag('HOME_AI_CARDS', true),

  /** Render the AccrualBar above the BalancesCard on the employee home
   *  page (annual leave only). The bar self-hides for tenured employees
   *  with no exit date, so this is invisible to the steady-state
   *  majority — exactly the surface area the §1.2 accrual states should
   *  expose. Defaults ON (kill-switch posture). Plan at
   *  docs/superpowers/plans/2026-05-28-accrual-rules-and-home-bars.md. */
  homeAccrualBars: flag('HOME_ACCRUAL_BARS', true),

  /** Health-bar chip on the manager request card — policy v1.0 §2.4.
   *  When ON: every pending leave row on the manager queue shows a
   *  `<HealthBarChip>` reflecting the requesting employee's outlook
   *  band (Red HIGH / Amber MEDIUM — LOW renders quietly with no chip).
   *  Backed by the new `healthBar` field on the `LeaveListItem` payload
   *  populated by `_attach_health_bars` in `services/api/src/routes/leaves.py`.
   *  Default ON per session preference (kill-switch posture); set
   *  `VITE_FEATURE_HEALTH_BAR_ON_REQUEST_CARD=false` in
   *  `frogasia/lms/web/<env>` to disable. */
  healthBarOnRequestCard: flag('HEALTH_BAR_ON_REQUEST_CARD', true),

  /** No-carry-forward forfeiture nudge on the My-Leave HealthBarCard
   *  — policy v1.0 §1.2. When ON: HealthBarCard renders an inline
   *  reminder ("6 days at risk of forfeit — 2 months left") whenever
   *  the api returns a non-`none` severity. The nudge does NOT feed
   *  the §2.3 score; it's a parallel surface on the same card.
   *  Default ON per session preference (kill-switch posture); set
   *  `VITE_FEATURE_FORFEIT_NUDGE=false` in `frogasia/lms/web/<env>`
   *  to disable. */
  forfeitNudge: flag('FORFEIT_NUDGE', true),

  /** Profile notification-preferences panel — migration 0104.
   *  When ON: the Notifications card on /profile renders real toggle
   *  switches backed by GET/PATCH /api/v1/me/notification-preferences.
   *  When OFF: the card renders read-only static defaults.
   *  Default ON per session preference (kill-switch posture); set
   *  `VITE_FEATURE_NOTIFICATION_PREFS=false` in
   *  `frogasia/lms/web/<env>` to disable. */
  notificationPrefs: flag('NOTIFICATION_PREFS', true),

  /** Real backend in-app notifications — migration 0130.
   *  When ON: the TopBar bell reads GET /api/v1/notifications (persisted
   *  rows, server-side read-state) and marks read via POST endpoints.
   *  When OFF: the bell falls back to the legacy client-side synthesis
   *  (poll /me/leaves + /escalations, localStorage read-state).
   *  Default ON (kill-switch posture); set
   *  `VITE_FEATURE_NOTIFICATIONS_BACKEND=false` in
   *  `frogasia/lms/web/<env>` to revert to the legacy path. */
  notificationsBackend: flag('NOTIFICATIONS_BACKEND', true),

  /** Profile emergency-contact inline edit — migration 0103.
   *  When ON: the Emergency Contact card on /profile renders editable
   *  text fields backed by PUT /api/v1/me/emergency-contact.
   *  When OFF: the card renders the existing read-only display.
   *  Default ON per session preference (kill-switch posture); set
   *  `VITE_FEATURE_EMERGENCY_CONTACT_EDIT=false` in
   *  `frogasia/lms/web/<env>` to disable. */
  emergencyContactEdit: flag('EMERGENCY_CONTACT_EDIT', true),

  /** Multi-select role assignment in the HR employee edit modal
   *  (lets one employee hold multiple roles, e.g. HR + Manager). Default ON
   *  (kill-switch posture); set `VITE_FEATURE_MULTI_ROLE=false` in
   *  `frogasia/lms/web/<env>` to disable. */
  multiRole: flag('MULTI_ROLE', true),

  /** HR "Import & Export" hub (/hr/import-export) — one sidebar entry with
   *  in-page tabs for the four data-migration surfaces (bulk import, import
   *  balances, import/export leave history). Consolidates four former nav
   *  items; each tab stays gated by its own per-surface flag. Default ON
   *  (kill-switch posture — reorganises already-shipped surfaces). */
  hrImportExportHub: flag('HR_IMPORT_EXPORT_HUB', true),

  /** Surfaces the existing bulk-import page (/hr/import) in the HR nav and as a
   *  button on /hr/employees. Default ON. */
  hrBulkImport: flag('HR_BULK_IMPORT', true),

  /** "Import org structure" tab in the Import & Export hub — one CSV
   *  (function,department,team) that bulk-creates the org-unit tree +
   *  teams via POST /api/v1/hr/org-structure/import. Structural only (no
   *  employees/approvers). Default ON (shipped + validated on dev); set
   *  `VITE_FEATURE_HR_ORG_STRUCTURE_IMPORT=false` in `frogasia/lms/web/<env>`
   *  to kill-switch it off. */
  hrOrgStructureImport: flag('HR_ORG_STRUCTURE_IMPORT', true),

  /** Surfaces the HR leave-balance migration import wizard (/hr/leave-import)
   *  in the HR nav. Migrates remaining leave credits from the old system via
   *  CSV upload. Default ON. */
  hrLeaveBalanceImport: flag('HR_LEAVE_BALANCE_IMPORT', true),

  /** Surfaces the HR leave-history migration import wizard
   *  (/hr/leave-history-import) in the HR nav. Imports past leave records from
   *  the old system via CSV; used/pending balances are recalculated from them
   *  (history-is-truth). Default ON. */
  hrLeaveHistoryImport: flag('HR_LEAVE_HISTORY_IMPORT', true),

  /** Surfaces the "Export leave history" card on /hr/leave-history-import.
   *  Downloads leave records as a CSV in the same layout the import reads, so
   *  HR can load them back into the legacy HRIS (round-trip). Default ON.
   *  Kill-switch OFF via VITE_FEATURE_HR_LEAVE_HISTORY_EXPORT=false. */
  hrLeaveHistoryExport: flag('HR_LEAVE_HISTORY_EXPORT', true),

  /** "Export Employees" tab in the Import & Export hub — downloads the whole
   *  employee directory as a CSV in the exact layout the importer reads, so HR
   *  can pull the master data out, edit it, and load it back in (round-trip).
   *  Backed by GET /api/v1/hr/employees/export (HR-only, audit-logged). Default
   *  ON; kill-switch OFF via VITE_FEATURE_HR_EMPLOYEE_EXPORT=false in
   *  frogasia/lms/web/<env>. */
  hrEmployeeExport: flag('HR_EMPLOYEE_EXPORT', true),

  /** Person-centric People Roster at `/manager/team` — Task 4 of the Manager
   *  Team Roster plan. Consumes `GET /api/v1/manager/team` (paginated roster
   *  with balances, health-bar, forfeiture severity, signals) and
   *  `GET /api/v1/manager/team/brief` (LLM-rephrased team summary). Default
   *  ON (kill-switch posture); set `VITE_FEATURE_MANAGER_TEAM_ROSTER=false`
   *  in `frogasia/lms/web/<env>` to disable. */
  managerTeamRoster: flag('MANAGER_TEAM_ROSTER', true),

  /** Org-head leave visibility. When ON, an org_unit head sees their subtree
   *  on the `/manager/team` People Roster — full detail — even people whose
   *  leave routes to a team manager, not to them: a `department` head sees
   *  their department, and the first `function` head above sees everyone down
   *  to (but not across) the next nested function. Capped there — functions /
   *  the company above that first function get nothing. The real gate is
   *  server-side (`ORG_HEAD_VISIBILITY_ENABLED` on the api, which both
   *  admits org-unit heads and widens the member set); this web flag mirrors it
   *  for the UI affordance. Default ON (ships enabled); kill-switch via
   *  `VITE_FEATURE_ORG_HEAD_VISIBILITY=false` in `frogasia/lms/web/<env>`
   *  alongside the api flag. */
  orgHeadVisibility: flag('ORG_HEAD_VISIBILITY', true),

  /** Requester-initiated cancellation of an APPROVED leave, routed to the
   *  manager for approval. When ON: the owner sees a "Request cancellation"
   *  control on their own approved leave (leave detail page + My Leave
   *  history) and must enter a short reason. The leave moves to
   *  `pending_cancellation`; the resolved manager(s) get an interactive Slack
   *  card + in-app notification and Approve / Reject from Slack or the leave
   *  detail page. Backed by `POST /api/v1/leaves/{id}/request-cancellation`
   *  + `/approve-cancellation` + `/reject-cancellation`. Default ON
   *  (kill-switch posture); set
   *  `VITE_FEATURE_CANCEL_APPROVED_REQUIRES_APPROVAL=false` in
   *  `frogasia/lms/web/<env>` to disable. */
  cancelApprovedRequiresApproval: flag('CANCEL_APPROVED_REQUIRES_APPROVAL', true),

  /** HR Integrations Email connector page (/hr/email) and nav item.
   *  When ON: HR-accessible page renders a read-only provider line, email_enabled
   *  toggle, editable FROM display name and reply-to inputs, a test-recipient
   *  input, and a "Send test email" button. Backed by
   *  PATCH /api/v1/org-config (email_enabled, email_from_name, email_reply_to) and
   *  POST /api/v1/hr/email/test (HR-accessible).
   *  Default ON (kill-switch posture); set
   *  VITE_FEATURE_EMAIL_SETTINGS=false in frogasia/lms/web/<env> to hide. */
  emailSettings: flag('EMAIL_SETTINGS', true),

  /** HOD "Return to manager" verb — Phase 2 of the Division Overview slice.
   *  When ON: an escalated leave row shows a "Return to manager" button that
   *  opens a modal requiring a cited reason (min 20 chars). On submit, the
   *  leave transitions ESCALATED → PENDING (back to the manager's inbox) and
   *  an audit row `hod.escalation.return_to_manager` is written.
   *  Backed by `POST /api/v1/hod/escalations/{id}/return` (Phase 2).
   *  Defaults OFF — new state behaviour, not yet built. Flip ON via
   *  `VITE_FEATURE_HOD_RETURN_TO_MANAGER=true` in `frogasia/lms/web/<env>`
   *  once the api route ships. */
  hodReturnToManager: flag('HOD_RETURN_TO_MANAGER', false),


  /** Whether employees may apply for unpaid leave. When ON, a discreet
   *  "Request unpaid leave" link renders on the Apply page below the leave-type
   *  picker (unpaid is intentionally kept OUT of the main dropdown — allowed
   *  but not obvious). Selecting it files a `type=unpaid` leave, which draws no
   *  paid balance and escalates to the Exec Director. When OFF the link never
   *  shows. Default OFF; enable via VITE_FEATURE_ALLOW_UNPAID_APPLICATION=true
   *  in frogasia/lms/web/<env>. */
  allowUnpaidApplication: flag('ALLOW_UNPAID_APPLICATION', false),

  /** Blackout gate — escalate vs block. Only Annual + Unpaid (the planned,
   *  discretionary leaves) are gated by a blackout overlap; every other type
   *  files normally. When ON, an Annual/Unpaid application overlapping an
   *  active blackout escalates to the Exec Director (existing flow); when OFF
   *  it is hard-blocked at submit. This flag is UI-only: it drives the
   *  disabled-submit affordance + block reason. The real block is enforced
   *  server-side (BLACKOUT_GATE_ESCALATE_ENABLED in the api config), so a
   *  stale web flag only ever degrades UX, never the enforcement. Default OFF
   *  (strict block); HR cannot toggle this in-app — it is deploy/env config. */
  blackoutGateEscalate: flag('BLACKOUT_GATE_ESCALATE', false),

  /** Unpaid "days taken" tile on the MY LEAVE grid (home). Unpaid leave has
   *  no entitlement/balance so it can't be an "of N left" tile; when ON, a
   *  usage tile shows unpaid days taken this year, fed by
   *  GET /api/v1/me/unpaid-usage. Default ON; kill-switch OFF via
   *  VITE_FEATURE_UNPAID_USAGE_TILE=false in frogasia/lms/web/<env>. */
  unpaidUsageTile: flag('UNPAID_USAGE_TILE', true),

  /** Negative accrual-stage annual balance display. When an employee is still
   *  inside their first-year monthly accrual window (accruedEntitlement <
   *  entitlement) and has booked more annual leave than they have accrued so
   *  far, show the true negative figure (e.g. −2, in the deep-red danger hue)
   *  instead of flooring it at 0. Annual leave only; steady-state tenured
   *  employees have accrued === entitlement so they are never affected. When ON
   *  the manager PersonModal and HR roster/detail also read the accrued-based
   *  remaining for first-year annual (consistent with what the employee sees),
   *  which lowers displayed remaining for first-year employees to accrued-so-far
   *  rather than the full slice. Backed by the additive `accruedEntitlement`
   *  field on the manager-roster + leave-credits matrix payloads. Default ON
   *  (kill-switch posture); set VITE_FEATURE_NEGATIVE_ACCRUAL_BALANCE=false in
   *  frogasia/lms/web/<env> to fall back to the 0-floor everywhere. */
  negativeAccrualBalance: flag('NEGATIVE_ACCRUAL_BALANCE', true),

  /** Out-of-system leave process (HR surfaces). When ON, HR can (1) encode a
   *  leave on an employee's behalf that the submit-time gates hard-block
   *  (unpaid / blackout), landing it APPROVED with a business-continuity
   *  document + justification, backed by POST /api/v1/hr/leaves/encode; and
   *  (2) is required to give a reason when raising a team's capacity on the
   *  coverage editor, so the audit trail captures who/why/when. Default ON
   *  (kill-switch); flip OFF via VITE_FEATURE_OUT_OF_SYSTEM_LEAVE=false in
   *  frogasia/lms/web/<env>. The api endpoint is separately gated by
   *  HR_OUT_OF_SYSTEM_ENCODE_ENABLED. */
  outOfSystemLeave: flag('OUT_OF_SYSTEM_LEAVE', true),

  /** HR capacity-change request history (Change requests tab on /hr/coverage).
   *  When ON, HR can record an offline-approved team capacity change with the
   *  requesting manager, justification, effectivity (permanent or a date range),
   *  and supporting documents — backed by /api/v1/hr/capacity-requests. A
   *  temporary date range is a live per-day override on the leave gate; a
   *  permanent change moves the team baseline. Default ON (kill-switch); flip
   *  OFF via VITE_FEATURE_CAPACITY_CHANGE_REQUESTS=false in
   *  frogasia/lms/web/<env>. The api endpoints are separately gated by
   *  CAPACITY_CHANGE_REQUESTS_ENABLED. */
  capacityChangeRequests: flag('CAPACITY_CHANGE_REQUESTS', true),

  /** HR-first review for document-required leave. When ON, a leave whose type
   *  requires a document (leave_types.document_required — MEDICAL,
   *  HOSPITALIZATION, MATERNITY, PATERNITY, MARRIAGE) lands in a
   *  `pending_hr_review` holding state on submit and appears in the HR review
   *  queue (the HR-only "HR Doc Review" tab on the Approvals page) instead of
   *  going straight to the manager. HR verifies the proof, then Forwards (→ pending, normal approver
   *  flow), Rejects (→ rejected), or Requests document (stays in the queue,
   *  notifies the employee). HR never appears in the approver-facing path — the
   *  manager only ever sees the leave once it is `pending`. Backed by
   *  `/api/v1/hr/leave-reviews`. Default ON (on by default; kill-switch) — flip
   *  OFF via `VITE_FEATURE_HR_DOCUMENT_REVIEW=false` in `frogasia/lms/web/<env>`
   *  to send document-required leaves straight to the manager as before. The api
   *  side is separately gated by `HR_DOCUMENT_REVIEW_ENABLED` (mirror this
   *  default). */
  hrDocumentReview: flag('HR_DOCUMENT_REVIEW', true),

  /** In-app Leave Policy Assistant — a floating chat widget on every page that
   *  answers HR leave-policy questions. Second transport for the same grounded
   *  Q&A the Slack Leave Assistant serves: backed by
   *  `POST /api/v1/assistant/ask`, which reuses the api's `build_policy_answer`
   *  (loads `leave_policy.md`, hops once to ai-service `/v1/policy-answer`, no
   *  callback — ADR-010). Conversation memory is session-only (the browser
   *  replays recent turns), so there is no server-side thread store. Default ON
   *  (kill-switch posture); flip OFF via `VITE_FEATURE_POLICY_ASSISTANT=false` in
   *  `frogasia/lms/web/<env>` to hide the widget in a given env. */
  policyAssistant: flag('POLICY_ASSISTANT', true),

  /** CSV export on the HR Leave Remaining Balance roster (`/hr/leaves` →
   *  Leave Remaining Balance tab). When ON, an "Export" button pages through
   *  every employee's remaining balance for the current year (respecting the
   *  active name search) and downloads a CSV in the same column layout as the
   *  on-screen table. Read-only, HR-scoped, no new backend endpoint (reuses
   *  `GET /api/v1/leave-credits/balances/matrix`). Default ON (kill-switch
   *  posture); flip OFF via `VITE_FEATURE_BALANCES_EXPORT=false` in
   *  `frogasia/lms/web/<env>`. */
  balancesExport: flag('BALANCES_EXPORT', true),

  /** Unified leave-eligibility surfacing. When ON: balance tiles for a type the
   *  employee is not yet eligible for (paternity/marriage/birthday under 12
   *  months' service, or during probation — surfaced via the `eligible` field on
   *  each balance row from GET /api/v1/me/balances) render a "NOT YET ELIGIBLE"
   *  badge regardless of the entitlement number, and the apply picker hides those
   *  types entirely. When OFF: tiles render as before and the picker uses only the
   *  legacy probation/gender/exhaustion filters. Server enforcement + the balance
   *  `eligible` field are always computed (gated server-side by
   *  LEAVE_ELIGIBILITY_GATES_ENABLED); this flag only drives the FE rendering.
   *  Default ON (kill-switch posture); set VITE_FEATURE_LEAVE_ELIGIBILITY_BADGE=false
   *  in frogasia/lms/web/<env> to hide the badge + fall back to legacy picker. */
  leaveEligibilityBadge: flag('LEAVE_ELIGIBILITY_BADGE', true),

  /** "Applied" column on the HR Applied Leaves table (`/hr/leaves` → Applied
   *  Leaves tab): shows when each leave was applied (the leave's created_at,
   *  served as `appliedAt` by GET /api/v1/hr/leaves), sortable client-side
   *  like the other columns. Default ON (kill-switch posture); flip OFF via
   *  `VITE_FEATURE_HR_LEAVES_APPLIED_COLUMN=false` in `frogasia/lms/web/<env>`. */
  hrLeavesAppliedColumn: flag('HR_LEAVES_APPLIED_COLUMN', true),

  /** Filter cards above the HR Applied Leaves table (`/hr/leaves` → Applied
   *  Leaves tab): Above capacity / Emergency / Out of system / Pending HR
   *  review. Each shows its count under the other filters (GET
   *  /api/v1/hr/leaves/counts) and toggles the list to that category
   *  (`?flag=`, one at a time). Default ON (kill-switch posture); flip OFF via
   *  `VITE_FEATURE_HR_LEAVES_FILTER_CARDS=false` in `frogasia/lms/web/<env>`. */
  hrLeavesFilterCards: flag('HR_LEAVES_FILTER_CARDS', true),

  /** Year filter (`‹ 2026 ›`) at the top of My Leaves and HR → Leaves →
   *  Applied Leaves. Scopes each list to leaves whose START date falls in the
   *  chosen year (default: current year; back to max(2025, current − 4)). On
   *  HR it rides the URL as `?year=` and is sent to GET /api/v1/hr/leaves and
   *  /counts; My Leaves filters client-side. Default ON (kill-switch posture);
   *  flip OFF via `VITE_FEATURE_LEAVE_YEAR_FILTER=false` in
   *  `frogasia/lms/web/<env>` to restore the all-years lists. */
  leaveYearFilter: flag('LEAVE_YEAR_FILTER', true),

  /** Work-from-home requests (`/wfh`). A WFH request is a work arrangement, not
   *  an absence, so it lives in its own surface backed by the separate
   *  `wfh_requests` model (POST /api/v1/wfh, /wfh/mine, /wfh/pending,
   *  /wfh/{id}/decide, /wfh/{id}/cancel) — it never touches leave balances or
   *  capacity. Gates the nav item, the employee apply/list page, and the
   *  manager approvals inbox. Default OFF; flip ON via
   *  VITE_FEATURE_WFH_REQUEST=true in frogasia/lms/web/<env>. This only hides the
   *  UI — the api's WFH_REQUESTS_ENABLED is the server-side half (404s the request
   *  routes when off), so turn both on together. */
  wfhRequest: flag('WFH_REQUEST', false),

  /** HR attendance register (`/wfh/register`) — every WFH / out-on-business
   *  record across all teams: outstanding (unreviewed) list, period/type/
   *  status filters, stat cards, CSV export. HR / Exec Director only. Backed
   *  by GET /api/v1/wfh/register. Default OFF; flip ON via
   *  VITE_FEATURE_WFH_REGISTER=true in frogasia/lms/web/<env>, together with the
   *  api's WFH_REQUESTS_ENABLED=true (which also gates /register). */
  wfhRegister: flag('WFH_REGISTER', false),
} as const

export type FeatureFlagKey = keyof typeof flags
export const __BUILD_TIMESTAMP__ = "2026-05-03T16:00Z"

